import styled from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';

export interface ISeparatorProps {
    showBeak?: boolean;
    inverseBeak?: boolean;
    orientation?: 'horizontal' | 'vertical';
    beakPosition?: 'start' | 'center' | 'flex-end';
    color?: string;
    beakColor?: string;
}

interface IBeakProps extends Pick<ISeparatorProps, 'inverseBeak' | 'orientation' | 'beakColor'> {
}

const FlexLine = styled.div.attrs({
    className: String('ap-separator')
}) <ISeparatorProps>`
    display: inline-flex;
    position: relative;
    width: ${({ orientation }) => (isVerticalOrientation(orientation!) ? '1px' : '100%')};
    height: ${({ orientation }) => (isVerticalOrientation(orientation!) ? '100%' : '1px')};
    background-color: ${({ color, theme: { semantics: { palette } } }) => color ?? palette.black};
    justify-content: ${({ orientation, beakPosition }) => (isVerticalOrientation(orientation!) ? 'unset' : beakPosition)};
    align-items: ${({ orientation, beakPosition }) => (isVerticalOrientation(orientation!) ? beakPosition : 'unset')};
`;

const getBeakDirection = memoizeFunction((orientation: 'horizontal' | 'vertical', isInverse?: boolean) => {
    if (isVerticalOrientation(orientation)) {
        if (isInverse) {
            return 'rotate(225deg)';
        }
        return 'rotate(45deg)';
    }
    if (isInverse) {
        return 'rotate(315deg)';
    }
    return 'rotate(135deg)';
});

const Beak = styled.div.attrs(() => ({
    className: String('ap-wc-separatorBeak')
})) <IBeakProps>`
    position: absolute;
    width: 15px;
    height: 15px;
    left: ${({ orientation }) => (isVerticalOrientation(orientation!) ? '-7px' : 'unset')};
    top: ${({ orientation }) => (isVerticalOrientation(orientation!) ? 'unset' : '-7px')};
    transform: ${({ inverseBeak, orientation }) => getBeakDirection(orientation!, inverseBeak)};
    background-color: ${({ beakColor, theme: { semantics: { palette } } }) => beakColor ?? palette.white};
    border-top: 1px solid ${({ color, theme: { semantics: { palette } } }) => color ?? palette.black};
    border-right: 1px solid ${({ color, theme: { semantics: { palette } } }) => color ?? palette.black};
`;

const isVerticalOrientation = memoizeFunction((orientation: 'horizontal' | 'vertical') => orientation === 'vertical');

export function Separator(props: ISeparatorProps) {
    const { orientation = 'vertical', showBeak = false, ...rest } = props;
    return (
        <FlexLine orientation={orientation} {...rest}>
            {!showBeak ? null :
                <Beak
                    color={props.color}
                    beakColor={props.beakColor}
                    orientation={props.orientation}
                    inverseBeak={props.inverseBeak} />}
        </FlexLine>
    );
}

export const VerticalSeparator = styled(Separator).attrs({
    orientation: 'vertical',
    beakPosition: 'center'
})`
    right: 0;
    position: absolute;
`;

export const HorizontalSeparator = styled(Separator).attrs({
    orientation: 'horizontal',
    beakPosition: 'center'
})`
    bottom: 0;
    position: absolute;
    opacity: 0.15;
    width: calc(100% - 30px);
`;
